/**
 * useVideoEffects Hook
 * React hook for integrating video effects with the video stream
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import {
  getVideoEffectsProcessor,
  cleanupVideoEffectsProcessor,
  VideoEffectsProcessor,
} from '../services/videoEffects';
import { useVideoEffectsStore } from '../contexts/videoEffectsStore';

export function useVideoEffects(sourceStream, enabled = false) {
  const processorRef = useRef(null);
  const videoRef = useRef(null);
  const [processedStream, setProcessedStream] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  const { effectType, blurLevel, selectedBackground, setIsProcessing, setIsSupported } =
    useVideoEffectsStore();

  // Check WebGL support on mount
  useEffect(() => {
    const supported = VideoEffectsProcessor.checkSupport();
    setIsSupported(supported);
    if (!supported) {
      setError('WebGL not supported on this device');
    }
  }, [setIsSupported]);

  // Initialize processor when enabled
  useEffect(() => {
    if (!enabled) return;

    const init = async () => {
      try {
        processorRef.current = getVideoEffectsProcessor();
        await processorRef.current.initialize();
        setIsInitialized(true);
        setError(null);
      } catch (err) {
        console.error('[useVideoEffects] Init error:', err);
        setError(err.message);
        setIsSupported(false);
      }
    };

    init();

    // NO cleanup here - we don't want to stop on every dependency change
  }, [enabled, setIsSupported]);

  // Create hidden video element for source when stream changes
  // Use a stable video element - don't recreate on every dependency change
  useEffect(() => {
    if (!sourceStream || !isInitialized || !enabled) {
      return;
    }

    // Only create video element if we don't have one
    if (!videoRef.current) {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
      videoRef.current = video;
      console.log('[useVideoEffects] Created new video element');
    }

    // Update srcObject if stream changed
    if (videoRef.current.srcObject !== sourceStream) {
      console.log('[useVideoEffects] Setting video srcObject to stream');
      videoRef.current.srcObject = sourceStream;
      videoRef.current.play().catch(console.error);

      // Monitor the source track for debugging
      const sourceTrack = sourceStream?.getVideoTracks()[0];
      if (sourceTrack) {
        sourceTrack.onended = () => {
          console.error('[useVideoEffects] SOURCE CAMERA TRACK ENDED! This should not happen.');
          console.trace('[useVideoEffects] Stack trace for source track ended');
        };
        sourceTrack.onmute = () => {
          console.warn('[useVideoEffects] Source camera track muted');
        };
        console.log('[useVideoEffects] Source track monitoring enabled, id:', sourceTrack.id);
      }
    }

    // NO cleanup that destroys the video element!
    // The processor needs it to keep running
  }, [sourceStream, isInitialized, enabled]);

  // Start processing and update effect type
  // IMPORTANT: Processing NEVER stops once started. When effectType is 'none', we use passthrough mode.
  // This is because mediasoup.replaceTrack() ends the old track, which would stop the camera.
  useEffect(() => {
    const processor = processorRef.current;

    if (!processor || !isInitialized) {
      return;
    }

    // Always update the effect type (processor handles 'none' as passthrough)
    processor.setEffect(effectType);

    // If not enabled (video disabled), don't start processing
    if (!enabled) {
      return;
    }

    // Track if this effect instance is still active
    let cancelled = false;

    // Wait for video element AND video to be ready
    const startWhenReady = () => {
      if (cancelled) return;

      const video = videoRef.current;

      // Wait for video element to be created
      if (!video) {
        console.log('[useVideoEffects] Waiting for video element...');
        setTimeout(startWhenReady, 100);
        return;
      }

      // Wait for video to have data
      if (video.readyState < 2 || video.videoWidth === 0) {
        console.log('[useVideoEffects] Video not ready, waiting... readyState:', video.readyState);
        setTimeout(startWhenReady, 100);
        return;
      }

      // Video is ready, start processing (if not already)
      if (!processor.isProcessing) {
        console.log('[useVideoEffects] Starting processing for effect:', effectType);
        // Reset debug flags for new processing session
        processor.debugMaskLogged = false;
        processor.debugSegmentationLogged = false;
        processor.frameCount = 0;
        const stream = processor.startProcessing(video);
        setProcessedStream(stream);
        console.log('[useVideoEffects] processedStream set, tracks:', stream?.getVideoTracks().length);
      } else {
        // Already processing - just update effect type (already done above)
        console.log('[useVideoEffects] Already processing, effect updated to:', effectType);
      }
      setIsProcessing(true);
    };

    startWhenReady();

    // Cleanup: cancel pending timeouts
    return () => {
      cancelled = true;
    };
  }, [effectType, isInitialized, enabled, setIsProcessing]);

  // Update blur level
  useEffect(() => {
    if (processorRef.current && effectType === 'blur') {
      processorRef.current.setBlurLevel(blurLevel);
    }
  }, [blurLevel, effectType]);

  // Update background image
  useEffect(() => {
    const updateBackground = async () => {
      if (processorRef.current && effectType === 'background') {
        if (selectedBackground) {
          try {
            await processorRef.current.setBackgroundImage(selectedBackground);
          } catch (err) {
            console.error('[useVideoEffects] Failed to load background:', err);
          }
        } else {
          processorRef.current.setBackgroundImage(null);
        }
      }
    };
    updateBackground();
  }, [selectedBackground, effectType]);

  // Get the appropriate stream - always return processed stream once processing started
  // This is because we never stop processing (mediasoup.replaceTrack ends old tracks)
  const getOutputStream = useCallback(() => {
    if (processedStream) {
      const tracks = processedStream.getVideoTracks();
      console.log('[useVideoEffects] getOutputStream returning PROCESSED stream, tracks:',
        tracks.length, 'state:', tracks[0]?.readyState, 'effect:', effectType);
      return processedStream;
    }
    if (sourceStream) {
      const tracks = sourceStream.getVideoTracks();
      console.log('[useVideoEffects] getOutputStream returning SOURCE stream, tracks:',
        tracks.length, 'state:', tracks[0]?.readyState);
    }
    return sourceStream;
  }, [effectType, processedStream, sourceStream]);

  // Get video track for MediaSoup - always return processed track once processing started
  const getOutputTrack = useCallback(() => {
    if (processorRef.current && processorRef.current.isProcessing) {
      const track = processorRef.current.getOutputTrack();
      if (track) return track;
    }
    return sourceStream?.getVideoTracks()[0];
  }, [sourceStream, processedStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupVideoEffectsProcessor();
    };
  }, []);

  return {
    processedStream,
    getOutputStream,
    getOutputTrack,
    isInitialized,
    isProcessing: processedStream !== null, // Processing never stops once started
    error,
  };
}

export default useVideoEffects;
